import { motion } from "framer-motion";
import { Check, ChevronDown, MessageCircle } from "lucide-react";
import { Button } from "./ui/button";

const HeroSection = ({ isLoaded }) => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const bulletPoints = [
    "Find better paying loads",
    "Keep 100% of your revenue",
    "Available 24/7",
    "No complicated apps",
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1714903726946-c307906c3b35?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80')`,
        }}
      >
        <div className="absolute inset-0 hero-gradient" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Text Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isLoaded ? "visible" : "hidden"}
            className="text-left"
          >
            <motion.span
              variants={itemVariants}
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              <MessageCircle className="w-4 h-4 text-green-400" />
              Dispatch via WhatsApp
            </motion.span>

            <motion.h1
              variants={itemVariants}
              className="font-heading text-5xl md:text-7xl font-bold text-white tracking-tight uppercase leading-tight mb-6"
            >
              Stop Paying<br />
              <span className="text-accent">10% Dispatch Fees.</span>
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-lg md:text-xl text-slate-200 leading-relaxed mb-8 max-w-xl"
            >
              MyLane finds your best loads, negotiates rates, and handles paperwork — all through WhatsApp.
            </motion.p>

            {/* Bullet Points */}
            <motion.ul variants={itemVariants} className="space-y-3 mb-10">
              {bulletPoints.map((point, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex items-center gap-3 text-white"
                >
                  <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-medium">{point}</span>
                </motion.li>
              ))}
            </motion.ul>

            {/* CTA Buttons */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                onClick={() => scrollToSection("contact")}
                className="bg-accent hover:bg-accent/90 text-white font-bold uppercase tracking-wide px-8 py-6 text-lg rounded-md btn-primary"
                data-testid="hero-cta-primary"
              >
                Join The Waitlist
              </Button>
              <Button
                onClick={() => scrollToSection("how-it-works")}
                variant="outline"
                className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 font-bold uppercase tracking-wide px-8 py-6 text-lg rounded-md"
                data-testid="hero-cta-secondary"
              >
                See How It Works
              </Button>
            </motion.div>
          </motion.div>

          {/* Right Column - Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:block relative"
          >
            <div className="relative">
              {/* Phone mockup with WhatsApp interface */}
              <div className="bg-white rounded-3xl shadow-2xl p-3 max-w-xs mx-auto transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <div className="bg-slate-100 rounded-2xl overflow-hidden">
                  {/* WhatsApp header */}
                  <div className="bg-[#075E54] px-4 py-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <span className="text-white font-bold">ML</span>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">MyLane Dispatch</p>
                      <p className="text-white/70 text-xs">Online</p>
                    </div>
                  </div>
                  
                  {/* Chat messages */}
                  <div className="p-4 space-y-3 min-h-[300px] bg-[#ECE5DD]">
                    <div className="bg-white rounded-lg p-3 max-w-[80%] shadow-sm">
                      <p className="text-sm text-slate-700">Hey! I'm in Chicago, available tomorrow morning.</p>
                      <p className="text-xs text-slate-400 text-right mt-1">9:41 AM</p>
                    </div>
                    
                    <div className="bg-[#DCF8C6] rounded-lg p-3 max-w-[80%] ml-auto shadow-sm">
                      <p className="text-sm text-slate-700">Perfect! Found 3 great loads for you:</p>
                      <p className="text-sm text-slate-700 mt-2 font-medium">1. Chicago → Detroit: $2,450</p>
                      <p className="text-sm text-slate-700 font-medium">2. Chicago → St. Louis: $1,890</p>
                      <p className="text-sm text-slate-700 font-medium">3. Chicago → Indianapolis: $1,650</p>
                      <p className="text-xs text-slate-400 text-right mt-1">9:42 AM</p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-3 max-w-[80%] shadow-sm">
                      <p className="text-sm text-slate-700">I'll take the Detroit run!</p>
                      <p className="text-xs text-slate-400 text-right mt-1">9:43 AM</p>
                    </div>
                    
                    <div className="bg-[#DCF8C6] rounded-lg p-3 max-w-[80%] ml-auto shadow-sm">
                      <p className="text-sm text-slate-700">Done! Rate confirmed at $2,450. Paperwork sent to your email.</p>
                      <p className="text-xs text-slate-400 text-right mt-1">9:45 AM</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-xl px-4 py-3"
              >
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Average savings</p>
                <p className="text-2xl font-heading font-bold text-accent">$2,500-$4,000/mo</p>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <button
            onClick={() => scrollToSection("problem")}
            className="text-white/60 hover:text-white transition-colors"
            data-testid="scroll-indicator"
          >
            <ChevronDown className="w-8 h-8 animate-bounce" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
